import { isReferralAllwaysRequired } from '@/v2/utils/commonServiceClassificationRulesUtils';
import { z } from 'zod';

export function createConsultationDetailsSchema({
  service,
  selectedTimeSlot,
  fileUploadRequired,
  t,
  requiredMessage,
  isConditionsMetForMRTContraindications,
  isFileRequiredForService,
  showConsultationReason,
}) {
  const isMrtFn =
    typeof isConditionsMetForMRTContraindications === 'function'
      ? isConditionsMetForMRTContraindications
      : () => !!isConditionsMetForMRTContraindications;

  const svcType = Number(service?.serviceTypeExternalId);
  const specialtyExtId = Number(service?.specialtyExternalId);

  const KT_ID = Number(process.env.REACT_APP_KT_SERVICE_TYPE_EXT_ID);
  const MRT_ID = Number(process.env.REACT_APP_MRT_SERVICE_TYPE_EXT_ID);
  const GSK_SERVICE_TYPE_ID = Number(
    process.env.REACT_APP_GSK_SERVICE_TYPE_EXT_ID,
  );
  const VGSK_SERVICE_TYPE_ID = Number(
    process.env.REACT_APP_VGSK_SERVICE_TYPE_EXT_ID,
  );
  const SG_SPECIALTY_EXTERNAL_ID = Number(
    process.env.REACT_APP_SG_SPECIALTY_EXTERNAL_ID,
  );
  const VACCINE_EXTERNAL_ID = Number(process.env.REACT_APP_VACCINEEXTERNALID);

  const isPaid = !!selectedTimeSlot?.isPaid;

  const shouldHideReferralSection =
    specialtyExtId === SG_SPECIALTY_EXTERNAL_ID ||
    (!isPaid && svcType === VACCINE_EXTERNAL_ID);

  const needFileBase =
    !shouldHideReferralSection &&
    (service?.isFileRequired ||
      isFileRequiredForService() ||
      isReferralAllwaysRequired(svcType)) &&
    fileUploadRequired;

  const needFileWithNotFamilyMsg =
    needFileBase && (svcType === KT_ID || svcType === MRT_ID);
  const needFileWithDoctorMsg = needFileBase && !needFileWithNotFamilyMsg;

  const isPecialistConsultation =
    svcType === GSK_SERVICE_TYPE_ID || svcType === VGSK_SERVICE_TYPE_ID;

  return z
    .object({
      selected: z.any().optional(),
      reason: z.string().optional(),
      eHealthReferral: z.any().optional(),
      paidReasonId: z.any().optional(),
      files: z.any().optional(),
      contraindicationQuestionIds: z
        .array(z.union([z.string(), z.number()]))
        .optional()
        .default([]),
    })
    .superRefine((data, ctx) => {
      const hasContraAnswers =
        (data.contraindicationQuestionIds ?? []).length > 0;

      const requireReason =
        showConsultationReason &&
        (service?.categoryId === 1 ||
          (service?.categoryId === 2 &&
            (isPaid || (isMrtFn() && hasContraAnswers))));

      if (requireReason) {
        const value = (data.reason ?? '').trim();
        if (!value) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['reason'],
            message: requiredMessage,
          });
        }
      }

      const files = Array.isArray(data.files) ? data.files : [];

      if (
        !shouldHideReferralSection &&
        !data.eHealthReferral &&
        needFileWithNotFamilyMsg &&
        files.length < 1
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['files'],
          message: t('validation.referralToDoctorNotFamily'),
        });
      } else if (
        !shouldHideReferralSection &&
        !data.eHealthReferral &&
        needFileWithDoctorMsg &&
        files.length < 1
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['files'],
          message: isPecialistConsultation
            ? t('validation.referralToRegistration')
            : t('validation.referralToDoctor'),
        });
      }
    });
}
